import React, { useState, useContext, useEffect, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { ActivitiesService, UserService } from 'gw-capability-gateway';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';

import metadata from './MLAddActivityPopup.metadata.json5';
import styles from './MLAddActivityPopup.module.scss';
import messages from './MLAddActivityPopup.messages';

const MLAddActivityPopup = (props) => {
    const {
        accountNumber,
        policyNumber,
        onCancel,
        onSave,
        actionBtnLabel,
        showValidationErrors,
        setShowValidationErrors,
        viewModelService,
        authHeader
    } = props;
    const { refreshData } = useDataRefresh();
    const translator = useTranslator();
    const { onValidate } = useValidation('MLAddActivityPopup');
    const [activityVM, updateActivityVM] = useState({});

    const priorityTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('Priority');
    const activityStatusTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('ActivityStatus');
    const [activityPatternCategories, updateActivityPatternCategories] = useState([])
    const [typeAvailableValues, updateTypeAvailableValues] = useState([]);
    const [priorityAvailableValues, updatePriorityAvailableValues] = useState([]);
    const [assignToAvailableValues, updateAssignToAvailableValues] = useState([]);
    const [assignToData, updateAssignToData] = useState([]);
    const [activityPatternData, updateActivityPatternData] = useState([]);
    const [selectedAssignment, updateSelectedAssignment] = useState([]);
    const [previousCategory, updatePreviousCategory] = useState();


    const activityEntity = accountNumber? 'account' : 'policy';
    const activityEntityId = accountNumber || policyNumber;
    const self = translator(messages.self);
    const [userToAssign, setUserToAssign] = useState({});
    const [assignableQueues, setAssignableQueues] = useState({});

    const createVM = useCallback(
        (model) => {
            return viewModelService.create(
                model,
                'pc',
                'edge.capabilities.gateway.activity.dto.ActivityDTO'
            );
        },
        [viewModelService]
    );

    const statusAvailableValues = activityStatusTypelist.codes.map((status) => {
        return translator({
            code: status.code,
            name: translator({
                id: status.name,
                defaultMessage: status.name
            })
        });
    });

    const getSelfFromAssignableUsers = useCallback(
        (assignableUsers, currentUser) => {
            return assignableUsers.find((u) => u.includes(currentUser))
        },[]
    );

    const getAssignToValuesForActivity = useCallback(
        async (activityPattern) => {
            const currentUser = await UserService.getCurrentUser(authHeader);
            const queues = await ActivitiesService.getAssignableQueuesForActivity(
                activityEntity,
                activityEntityId,
                authHeader
            );
            const assignableUsers = await ActivitiesService.getAssignableUserForActivity(
                activityEntity,
                activityEntityId,
                activityPattern,
                authHeader
            );
            // } else {
            //     activity = await ActivitiesService.getAssignableUserForActivity(
            //         "policy",
            //         policyNumber,
            //         activityPattern,
            //         authHeader
            //     );
            // }

            const showSelfOption = getSelfFromAssignableUsers(assignableUsers.assignableUsers, currentUser.displayName)
            updateAssignToData(showSelfOption ? [{displayName: self, name: self}].concat(queues) : queues);
        },
        [activityEntity, activityEntityId, authHeader, getSelfFromAssignableUsers, self]
    );

    const getSelfUser = useCallback(async () => {
        try {
            const [currentUser, activityPatterns] = await Promise.all([
                UserService.getCurrentUser(authHeader),
                ActivitiesService.getActivityPatternsFor(
                    activityEntity,
                    authHeader
                ),
            ]);
      
            if (!activityPatterns?.length) {
                setUserToAssign(null);
                return;
            }
      
            const { assignableUsers } =
                await ActivitiesService.getAssignableUserForActivity(
                    activityEntity,
                    activityEntityId,
                    activityPatterns[0],
                    authHeader
                );
            const user = getSelfFromAssignableUsers(
                assignableUsers,
                currentUser.displayName
            );

            setUserToAssign(user);
        } catch (error) {
            console.error("Failed to get self user", error);
        }
        },
        [activityEntity, activityEntityId, authHeader, getSelfFromAssignableUsers]
    );

    const getAvailableQueues = useCallback(
        async () => {
            const queues = await ActivitiesService.getAssignableQueuesForActivity(
                activityEntity,
                activityEntityId,
                authHeader
            );

            setAssignableQueues(queues)
        },
        [activityEntity, activityEntityId, authHeader, setAssignableQueues]
    );

    const getActivityPatternsFor = useCallback(
        async () => {
            const activityPatterns = await ActivitiesService.getActivityPatternsFor(activityEntity, authHeader);

            // getAssignableUserForActivity(activityPatterns[0]);   
            getAssignToValuesForActivity(activityPatterns[0]);     
            updateActivityPatternData([...activityPatterns]);
        },
        [activityEntity, authHeader, getAssignToValuesForActivity]
    );

    const setTypesForCategory = useCallback(
        (category) => {
            const typeDataArray = activityPatternData.filter((key) => key.category.code === category).map((key) => {
                return {
                    name: key.subject,
                    code: key.subject
                };
            });
            updateTypeAvailableValues(typeDataArray);
        },
        [activityPatternData]
    );

    useEffect(() => {
        updateActivityVM(createVM({
            status: 'open'
        }));
        getSelfUser();
        getAvailableQueues();
        getActivityPatternsFor();    

    }, [createVM, getActivityPatternsFor, getSelfUser, getAvailableQueues]);

    const handleChange = useCallback(
        (value, path) => {
            const newActivitySubmissionVM = viewModelService.clone(activityVM);
            let newActivityResult = [];
            _.set(newActivitySubmissionVM.value, path, value !== '' ? value : undefined);
            switch (path) {
                case 'activityPattern':
                    setTypesForCategory(value)
                    break;
                case 'jobType':
                    if(value) {
                        newActivityResult = activityPatternData.find((activity) => {
                            return activity.subject === value;
                        });
                        _.set(newActivityResult, 'jobType', value);
                        delete newActivityResult.publicID;
                        const newValue = _.assign(newActivitySubmissionVM.value, newActivityResult)
                        _.set(newActivitySubmissionVM, 'value', newValue);
                    }
                    break;
                case 'assignedTo':
                    updateSelectedAssignment(value);
                    _.set(newActivitySubmissionVM, 'assignedTo', value);
                    break;
                case 'dueDate': {
                    const today = new Date();
                    const time = `${today.getHours() }:${ today.getMinutes() }:${ today.getSeconds()}`;
                    const changeDate = new Date(`${value.year}-${value.month+1}-${value.day} ${time}`);
                    const dueDate = changeDate.toISOString();
                    _.set(newActivitySubmissionVM, 'dueDate', dueDate);
                    break;
                }
                default:
                    break;
            }
            updateActivityVM(newActivitySubmissionVM);
            const currentCategory = _.get(activityVM.value, 'activityPattern')
            updatePreviousCategory(currentCategory || previousCategory)
            refreshData();
        },
        [activityPatternData, activityVM, previousCategory, refreshData, setTypesForCategory, viewModelService]
    );

    const handleSave = async () => {
        // TODO: have dropdown handle shape of activityPattern object
        // instead of cleaning up the object here for validation
        const cloneVm = viewModelService.clone(activityVM);
        const newActivityPattern = {
            code: _.get(activityVM.value, 'code'),
            priority: _.get(activityVM.value, 'priority')
        }
        _.set(cloneVm, 'value.activityPattern', newActivityPattern)

        const isValid = (_.get(cloneVm, 'aspects.valid') && _.get(cloneVm, 'aspects.subtreeValid'))
        if (!isValid) {
            setShowValidationErrors(true)
            return
        }

        const isSelf = selectedAssignment.name === self;
        let newActivityData = {
            ..._.omit(activityVM.value, 'assignedTo'),
            selectedAssignee: isSelf ? userToAssign : undefined,
            selectedQueue_ML: !isSelf ? assignableQueues.find((u) => u.displayName.includes(selectedAssignment)) : undefined,
            status: 'open',
            activityPattern: newActivityPattern
        };
        newActivityData = _.omit(newActivityData, 'jobType');

        if (accountNumber) {
            newActivityData.accountNumber = accountNumber;
        } else {
            newActivityData.policyNumber = policyNumber;
        }
        
        const addResponse = await ActivitiesService.createNewActivity(
            newActivityData,
            null,
            authHeader
        );
        
        if (addResponse) {
            await onSave();
        }
    }

    useEffect(() => {
        const uniqueCategory = _.uniqBy(activityPatternData, 'category.code');
        uniqueCategory && uniqueCategory.reverse 
        const categoryData = uniqueCategory.map((key) => {
            return {
                name: key.category.name,
                code: key.category.code
            };
        });

        const priorityDataArray = priorityTypelist.codes.map((priority) => {
            return translator({
                code: priority.code,
                name: translator({
                    id: _.startCase(_.toLower(priority.code)),
                    defaultMessage: _.startCase(_.toLower(priority.code))
                })
            });
        });

        const assignToDataObject = assignToData.map((key) => {
            return {
                name: key.displayName,
                code: key.name
            };
        });
        const defaultAssignment = assignToDataObject[0];
        updatePriorityAvailableValues(priorityDataArray);
        updateAssignToAvailableValues(assignToDataObject);
        updateSelectedAssignment(defaultAssignment);
        updateActivityPatternCategories(categoryData);
    }, [activityPatternData, assignToData, priorityTypelist, translator]);


    const overrideProps = {
        type: {
            availableValues: typeAvailableValues,
            value: _.get(activityVM.value, 'jobType')
        },
        category: {
            availableValues: activityPatternCategories,
            value: _.get(activityVM.value, 'activityPattern') || previousCategory
        },
        status: {
            value: _.get(activityVM, 'status.value'),
            availableValues: statusAvailableValues
        },
        priority: {
            value: _.get(activityVM, 'priority.value'),
            availableValues: priorityAvailableValues
        },
        assignedTo: {
            availableValues: assignToAvailableValues,
            value: selectedAssignment
        },
        closeButton: {
            content: translator(messages.cancel)
        },
        saveButton : {
            content : actionBtnLabel || translator(messages.saveBtn)
        }
    };

    const resolvers = {
        resolveCallbackMap: {            
            onClose: onCancel,
            onSave: handleSave
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={activityVM}
            onValueChange={handleChange}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={styles}
            onValidationChange={onValidate}
            showErrors={showValidationErrors}
        />
    );
};

MLAddActivityPopup.propTypes = {
    accountNumber: PropTypes.string,
    policyNumber: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    actionBtnLabel: PropTypes.string.isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
    setShowValidationErrors: PropTypes.func.isRequired
};

export default MLAddActivityPopup;
